import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import i18n from '../i18n';
import { useAuthStore } from '../shared/store/useAuthStore';

const SettingsContext = createContext();

const ONLY_MY_EVENTS_KEY = 'onlyMyEvents';

const getStoredOnlyMyEvents = () => {
    return false;
};

export const SettingsProvider = ({ children }) => {
    // null = no settings open (normal view)
    // 'workspace', 'team', etc. = specific setting open
    const [activeSetting, setActiveSetting] = useState(null);

    // Default-on filter for /api/events: only show the logged-in user's own
    // events (owner, creator, assignee or attendee). Persisted per-browser.
    const [onlyMyEvents, setOnlyMyEventsState] = useState(getStoredOnlyMyEvents);

    const setOnlyMyEvents = useCallback((value) => {
        setOnlyMyEventsState(value);
        localStorage.setItem(ONLY_MY_EVENTS_KEY, String(value));
    }, []);

    const closePanel = useCallback(() => setActiveSetting(null), []);

    // frontend-restructuring Fase 3 (03.1-auth-store.md §4): puerto directo de
    // `UserContext.jsx:471-497` -- solo la ACCIÓN se muda acá, el VALOR (`user.language`) se
    // queda leyéndose del auth store (5 archivos reales lo leen directo, moverlo también habría
    // sido un scope distinto). i18n (2026-07-12): cambia el idioma de la UI al instante y lo
    // persiste tanto local (localStorage, sobrevive un refresh sin esperar la red) como en el
    // servidor (para que sobreviva a un login desde otro dispositivo/navegador).
    const setLanguage = useCallback(async (language) => {
        i18n.changeLanguage(language);
        // Key dedicada, no anidada en `currentUser` -- `updateUserProfile` sobreescribe todo el
        // blob de `currentUser` con la respuesta del servidor, que no trae `language`.
        localStorage.setItem('appLanguage', language);
        useAuthStore.setState((state) => ({ user: { ...state.user, language } }));

        try {
            await fetch('/api/users/me/preferences', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ language }),
            });
        } catch (err) {
            console.error('Error saving language preference', err);
        }

        // Varios componentes (Profile, Spaces dashboard) todavía tienen texto fijo en español
        // que no pasa por `t()` -- un reload completo (una sola vez, al cambiar de idioma) fuerza
        // que todo lo que lee `i18n.language` en su render inicial quede consistente.
        window.location.reload();
    }, []);

    // Puerto directo de `UserContext.jsx:504-534` -- mismo patrón que `setLanguage`, pero no
    // hace falta un reload completo (nada más en la UI lee `user.timezone` en su render inicial
    // fuera de lo que ya está suscrito al store).
    const setTimezone = useCallback(async (timezone) => {
        useAuthStore.setState((state) => {
            const saved = localStorage.getItem('currentUser');
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    parsed.timezone = timezone;
                    localStorage.setItem('currentUser', JSON.stringify(parsed));
                } catch (e) {
                    console.error('Error saving timezone to localStorage', e);
                }
            }
            return { user: { ...state.user, timezone } };
        });

        try {
            const response = await fetch('/api/users/me/preferences', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ timezone }),
            });
            const data = await response.json();
            if (!data.success) {
                console.error('Error saving timezone preference', data.error);
            }
        } catch (err) {
            console.error('Error saving timezone preference', err);
        }
    }, []);

    // Perf (fase 43.2): antes este objeto se recreaba en cada render de `SettingsProvider`,
    // re-renderizando a todos sus consumidores (Menu1, Searcher, OnlyMineToggle, etc.) aunque
    // ni `activeSetting` ni `onlyMyEvents` hubieran cambiado. Mismo patrón que
    // `UserContext.jsx`: las funciones ya estabilizadas con `useCallback` arriba hacen que este
    // `useMemo` solo invalide cuando algún valor real cambia.
    const contextValue = useMemo(() => ({
        activeSetting, setActiveSetting, closePanel, onlyMyEvents, setOnlyMyEvents, setLanguage, setTimezone
    }), [activeSetting, setActiveSetting, closePanel, onlyMyEvents, setOnlyMyEvents, setLanguage, setTimezone]);

    return (
        <SettingsContext.Provider value={contextValue}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
